import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "May 1, 2025";
const COMPANY = "GetCitizen AI";
const EMAIL = "legal@getcitizen.ai";

export function TermsPage() {
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <PublicHeader />

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-card p-8 sm:p-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">
            Legal
          </p>
          <h1 className="text-3xl font-bold font-display text-slate-900 mb-1">
            Terms of Service
          </h1>
          <p className="text-sm text-slate-400 mb-10">
            Effective {EFFECTIVE_DATE}
          </p>

          <Prose>
            <Section title="1. Acceptance of Terms">
              By accessing or using {COMPANY} ("the Service"), you agree to be bound by
              these Terms of Service. If you do not agree, do not use the Service.
            </Section>

            <Section title="2. Description of Service">
              {COMPANY} is a software tool that helps individuals prepare USCIS Form N-400
              (Application for Naturalization). The Service generates a pre-filled PDF
              based on information you provide. The Service is <strong>not</strong> a law
              firm and does not provide legal advice. Use of this Service does not create
              an attorney-client relationship.
            </Section>

            <Section title="3. Not Legal Advice">
              The information and forms generated by the Service are provided for
              informational purposes only and do not constitute legal advice. Immigration
              law is complex and fact-specific. We strongly recommend consulting a
              qualified immigration attorney or accredited representative before
              submitting any application to USCIS.
            </Section>

            <Section title="4. Eligibility">
              You must be at least 18 years old to use the Service. By using the Service,
              you represent that all information you provide is accurate, current, and
              complete. You are solely responsible for the accuracy of your submissions.
            </Section>

            <Section title="5. Payments and Refunds">
              Access to the completed N-400 packet requires a one-time filing fee payment
              processed via Stripe. Payments are non-refundable once the PDF packet has
              been generated and made available for download. If you experience a technical
              issue preventing download, contact us at {EMAIL} within 7 days.
            </Section>

            <Section title="6. Prohibited Uses">
              You may not use the Service to submit false information to any government
              agency, impersonate another individual, or violate any applicable law or
              regulation. We reserve the right to terminate accounts that violate these
              terms.
            </Section>

            <Section title="7. Intellectual Property">
              All software, design, and content comprising the Service are owned by
              {" "}{COMPANY} and protected by copyright law. You may not reproduce,
              distribute, or create derivative works without written permission.
            </Section>

            <Section title="8. Disclaimer of Warranties">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. WE DO NOT
              WARRANT THAT THE SERVICE WILL BE ERROR-FREE, UNINTERRUPTED, OR THAT ANY
              GENERATED FORMS WILL BE ACCEPTED BY USCIS.
            </Section>

            <Section title="9. Limitation of Liability">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} SHALL NOT
              BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES
              ARISING FROM YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY
              OF SUCH DAMAGES.
            </Section>

            <Section title="10. Changes to Terms">
              We may update these Terms at any time. Continued use of the Service after
              changes constitutes acceptance of the revised Terms. We will note the
              effective date at the top of this page.
            </Section>

            <Section title="11. Contact">
              Questions about these Terms? Email us at{" "}
              <a href={`mailto:${EMAIL}`} className="text-blue-600 hover:underline">
                {EMAIL}
              </a>.
            </Section>
          </Prose>

          <div className="mt-10 pt-6 border-t border-slate-100 flex items-center gap-4 text-sm">
            <Link to="/privacy" className="text-blue-600 hover:text-blue-700">
              Privacy Policy →
            </Link>
            <Link to="/dashboard" className="text-slate-400 hover:text-slate-600 ml-auto">
              ← Back to app
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PublicHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200/60 shadow-sm">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shadow-sm transition-transform group-hover:scale-105">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 1v14M1 8h14M4.5 4.5l7 7M11.5 4.5l-7 7" />
            </svg>
          </div>
          <span className="text-base font-bold tracking-tight text-slate-900 font-display">
            GetCitizen <span className="text-blue-600">AI</span>
          </span>
        </Link>
      </div>
    </header>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-7 text-slate-700 text-sm leading-relaxed">
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-semibold font-display text-slate-900 text-base mb-2">{title}</h2>
      <p>{children}</p>
    </div>
  );
}
